"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateMany = exports.updateOne = exports.getTotal = exports.getPage = exports.getMany = exports.getOne = exports.deleteMany = exports.deleteOne = exports.createMany = exports.createOne = void 0;
const enrich_sdk_1 = require("@groupby/enrich-sdk");
const stdlib_core_1 = require("@groupby/stdlib-core");
const partial_apply_1 = require("@groupby/stdlib-core/#lib/functions/partial-apply");
const page_1 = require("@groupby/stdlib-core/collections/page");
const stdlib_http_1 = require("@groupby/stdlib-http");
const constants_1 = require("@groupby/stdlib-jsonapi/constants");
const cross_fetch_1 = require("cross-fetch");
const qs_1 = __importDefault(require("qs"));
/** @hidden */
async function createOne(config, deps, entity) {
    const entityArray = await createMany(config, deps, [entity]);
    return entityArray[0];
}
exports.createOne = createOne;
/** @hidden */
async function createMany(config, deps, entities) {
    const resourceEntities = entities.map((entity) => deps.toResource(entity));
    const requestDocument = { data: resourceEntities };
    const headers = createDefaultHeaders(config);
    headers.append('Content-Type', constants_1.mediaType);
    const fetchParams = { method: stdlib_http_1.Method.Post, body: JSON.stringify(requestDocument), headers };
    const response = await deps.fetch(config.baseUrl, fetchParams);
    const document = await getDocument(response);
    assertResponse(response.status, document);
    const resources = document.data;
    return resources.map(deps.fromResource);
}
exports.createMany = createMany;
/** @hidden */
async function deleteOne(config, deps, entityId) {
    const url = `${config.baseUrl}/${entityId}`;
    const fetchParams = { method: stdlib_http_1.Method.Delete, headers: createDefaultHeaders(config) };
    const response = await deps.fetch(url, fetchParams);
    assertResponse(response.status, await getDocument(response));
}
exports.deleteOne = deleteOne;
/** @hidden */
async function deleteMany(config, deps, filter) {
    const queryParams = mapToApiQueryParams({ filter });
    const url = [config.baseUrl, qs_1.default.stringify(enrich_sdk_1.formatCollectionQueryParameters(queryParams))].filter(Boolean).join('?');
    const fetchParams = { method: stdlib_http_1.Method.Delete, headers: createDefaultHeaders(config) };
    const response = await deps.fetch(url, fetchParams);
    const document = await getDocument(response);
    assertResponse(response.status, document);
    return document.meta.total;
}
exports.deleteMany = deleteMany;
/** @hidden */
async function getOne(config, deps, entityId) {
    const url = `${config.baseUrl}/${entityId}`;
    const fetchParams = { method: stdlib_http_1.Method.Get, headers: createDefaultHeaders(config) };
    const response = await deps.fetch(url, fetchParams);
    const document = await getDocument(response);
    assertResponse(response.status, document);
    return deps.fromResource(document.data);
}
exports.getOne = getOne;
/** @hidden */
async function getMany(config, deps, query = {}) {
    const document = await requestCollection(config, deps, query, undefined);
    const resources = document.data;
    return resources.map(deps.fromResource);
}
exports.getMany = getMany;
/** @hidden */
async function getPage(config, deps, query = {}) {
    var _a, _b;
    const document = await requestCollection(config, deps, query, true);
    const resources = document.data;
    const entities = resources.map(deps.fromResource);
    const total = document.meta.total;
    return new page_1.ArrayPage(entities, (_b = (_a = query.page) === null || _a === void 0 ? void 0 : _a.offset) !== null && _b !== void 0 ? _b : 0, total);
}
exports.getPage = getPage;
/** @hidden */
async function getTotal(config, deps, filter) {
    const document = await requestCollection(config, deps, { filter, page: { limit: 0 } }, true);
    return document.meta.total;
}
exports.getTotal = getTotal;
/** @hidden */
async function updateOne(config, deps, entity) {
    const requestDocument = { data: deps.toResource(entity) };
    const headers = createDefaultHeaders(config);
    headers.append('Content-Type', constants_1.mediaType);
    const fetchParams = { method: stdlib_http_1.Method.Patch, body: JSON.stringify(requestDocument), headers };
    const response = await deps.fetch(config.baseUrl, fetchParams);
    const document = await getDocument(response);
    assertResponse(response.status, document);
    return deps.fromResource(document.data);
}
exports.updateOne = updateOne;
/** @hidden */
async function updateMany(config, deps, entities) {
    return Promise.all(entities.map(partial_apply_1.partialApply(updateOne, config, deps)));
}
exports.updateMany = updateMany;
async function requestCollection(config, deps, query, includeTotal) {
    const queryParams = { ...mapToApiQueryParams(query), includeTotal };
    const url = [config.baseUrl, qs_1.default.stringify(enrich_sdk_1.formatCollectionQueryParameters(queryParams))].filter(Boolean).join('?');
    const fetchParams = { method: stdlib_http_1.Method.Get, headers: createDefaultHeaders(config) };
    const response = await deps.fetch(url, fetchParams);
    const document = await getDocument(response);
    assertResponse(response.status, document);
    return document;
}
function createDefaultHeaders(config) {
    const headers = new cross_fetch_1.Headers();
    if (config.token) {
        headers.append('Authorization', `Bearer ${config.token}`);
    }
    return headers;
}
function mapToApiQueryParams(query) {
    var _a, _b;
    return {
        filter: query.filter,
        page: {
            limit: (_a = query.page) === null || _a === void 0 ? void 0 : _a.limit,
            offset: (_b = query.page) === null || _b === void 0 ? void 0 : _b.offset,
        },
        sort: query.sort,
    };
}
async function getDocument(response) {
    return response.status === stdlib_http_1.StatusCode.NoContent ? undefined : response.json();
}
function assertResponse(statusCode, document) {
    const statusClass = stdlib_http_1.inferStatusClass(statusCode);
    if (isErrorClass(statusClass)) {
        // We can assume the document is an ErrorDocument based on the status code's class.
        const errorMessage = getErrorMessage(document);
        generateExceptionFromStatusCode(statusCode, errorMessage);
        generateExceptionFromStatusClass(statusClass, errorMessage);
    }
}
function isErrorClass(statusClass) {
    return statusClass === stdlib_http_1.StatusClass.ClientError || statusClass === stdlib_http_1.StatusClass.ServerError;
}
function getErrorMessage(document) {
    return document.errors
        .map((error) => error.detail)
        .filter((msg) => msg != null)
        .join('\n');
}
function generateExceptionFromStatusCode(statusCode, errorMessage) {
    if (statusCode === stdlib_http_1.StatusCode.NotFound) {
        throw new stdlib_core_1.NotFoundException(errorMessage);
    }
}
function generateExceptionFromStatusClass(statusClass, errorMessage) {
    switch (statusClass) {
        case stdlib_http_1.StatusClass.ClientError:
            throw new stdlib_core_1.IllegalArgumentException(errorMessage);
        case stdlib_http_1.StatusClass.ServerError:
            throw new stdlib_core_1.Exception(errorMessage);
    }
}
//# sourceMappingURL=jsonapi.js.map